import { useMemo, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  CheckCircle2,
  Clock3,
  FileStack,
  Loader2,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";
import AdminLayout from "../admin/AdminDashboardLayout";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useMutation";
import DraftBatchSubmissionModal from "../../components/canvasser/DraftBatchSubmissionModal";

type BatchStatus = "DRAFT" | "SUBMITTED" | "VERIFIED" | "REJECTED" | string;

type BatchRequest = {
  id: number;
  partnerId: number;
  canvasserId: number;
  claimedForms: number;
  verifiedForms: number;
  rejectedForms: number;
  incompleteForms: number;
  status: BatchStatus;
  verificationMode: string | null;
  verifiedByUserId: number | null;
  verifiedAt: string | null;
  submittedAt: string | null;
  overdueFlag: boolean;
  hoursToVerify: number | null;
  accuracyScore: number | null;
  rejectionRate: number | null;
  timelinessScore: number | null;
  finalQualityScore: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

type BatchRequestsResponse = {
  success: boolean;
  message: string;
  data: BatchRequest[];
};

const getStatusClasses = (status: string) => {
  switch (status?.toUpperCase()) {
    case "VERIFIED":
      return "border border-emerald-200 bg-emerald-50 text-emerald-700";
    case "SUBMITTED":
      return "border border-blue-200 bg-blue-50 text-blue-700";
    case "REJECTED":
      return "border border-rose-200 bg-rose-50 text-rose-700";
    default:
      return "border border-amber-200 bg-amber-50 text-amber-700";
  }
};

const formatDateValue = (value: string | null) => {
  if (!value) return "Not available";

  try {
    const date = new Date(value);
    const now = new Date();
    const distance = formatDistanceToNow(date, { addSuffix: true });
    return distance;
  } catch {
    return value;
  }
};

const formatScore = (value: number | null) => {
  if (value === null || value === undefined) return "--";
  return `${Number(value).toFixed(1)}%`;
};

const CanvasserBatchRequests = () => {
  const [submittingDraftBatchId, setSubmittingDraftBatchId] = useState<number | null>(null);
  const [draftSubmissionBatch, setDraftSubmissionBatch] = useState<BatchRequest | null>(null);

  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useApiQuery<BatchRequestsResponse>({
    queryKey: ["paper-form-batches", "my"],
    request: {
      method: "GET",
      url: "/paper-form-batches/my",
    },
  });

  const batches = response?.data ?? [];

  const submitDraftBatchMutation = useApiMutation<{
    success: boolean;
    message?: string;
  }>({
    onSuccess: async (result) => {
      toast.success(result?.message || "Batch draft submitted successfully", {
        id: "canvasser-batch-draft-submission-toast",
      });
      setDraftSubmissionBatch(null);
      await refetch();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit batch draft";
      toast.error(errorMessage, { id: "canvasser-batch-draft-submission-toast" });
    },
  });

  const handleSubmitBatchDraft = async (supervisorId: number | null) => {
    if (!draftSubmissionBatch) return;

    setSubmittingDraftBatchId(draftSubmissionBatch.id);

    try {
      toast.loading("Submitting batch draft...", {
        id: "canvasser-batch-draft-submission-toast",
      });

      await submitDraftBatchMutation.mutateAsync({
        method: "POST",
        url: `/paper-form-batches/${draftSubmissionBatch.id}/submit-draft`,
        data: supervisorId !== null ? { supervisorId } : undefined,
      });
    } finally {
      setSubmittingDraftBatchId(null);
    }
  };

  // Check if any batch has quality scores to show the Quality column
  const hasQualityScores = useMemo(() => {
    return batches?.some(
      batch => 
        batch.accuracyScore !== null && batch.accuracyScore !== undefined ||
        batch.rejectionRate !== null && batch.rejectionRate !== undefined ||
        batch.timelinessScore !== null && batch.timelinessScore !== undefined ||
        batch.finalQualityScore !== null && batch.finalQualityScore !== undefined
    );
  }, [batches]);

  const summary = useMemo(() => {
    return batches?.reduce(
      (acc, batch) => {
        acc.totalBatches += 1;
        acc.claimedForms += batch.claimedForms || 0;
        acc.verifiedForms += batch.verifiedForms || 0;

        if (batch.status?.toUpperCase() === "DRAFT") acc.draftBatches += 1;
        if (batch.status?.toUpperCase() === "PENDING") acc.pendingReview += 1;
        if (batch.status?.toUpperCase() === "VERIFIED") acc.verifiedBatches += 1;

        return acc;
      },
      {
        totalBatches: 0,
        claimedForms: 0,
        verifiedForms: 0,
        draftBatches: 0,
        pendingReview: 0,
        verifiedBatches: 0,
      }
    );
  }, [batches]);

  return (
    <AdminLayout role="CANVASSER">
      <div className="min-h-screen rounded-2xl bg-slate-50">
        <div className="mx-auto w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_18px_50px_-28px_rgba(15,23,42,0.35)]">
            <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.15),_transparent_35%),linear-gradient(135deg,_#f8fafc_0%,_#eef6ff_55%,_#ffffff_100%)] px-6 py-7 sm:px-8">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-2xl">
                  <p className="text-xs font-semibold uppercase tracking-[0.32em] text-sky-700">
                    Paper Form Batches
                  </p>
                  <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                    Batch Requests
                  </h1>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Review every batch you have created, monitor verification progress,
                    and keep track of submitted paper forms in one structured view.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => refetch()}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-sky-300 hover:text-sky-700"
                >
                  <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                  Refresh
                </button>
              </div>
            </div>

            <div className="grid gap-4 p-6 sm:grid-cols-2 xl:grid-cols-4 sm:p-8">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 outline outline-1 outline-slate-100">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-sky-100 p-3 text-sky-700">
                    <FileStack className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total batches</p>
                    <p className="text-2xl font-semibold text-slate-900">{summary.totalBatches}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 outline outline-1 outline-slate-100">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-amber-100 p-3 text-amber-700">
                    <Clock3 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Awaiting review</p>
                    <p className="text-2xl font-semibold text-slate-900">{summary.pendingReview}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 outline outline-1 outline-slate-100">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-700">
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Verified forms</p>
                    <p className="text-2xl font-semibold text-slate-900">{summary.verifiedForms}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 outline outline-1 outline-slate-100">
                <div className="flex items-center gap-3">
                  <div className="rounded-2xl bg-violet-100 p-3 text-violet-700">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Collected forms</p>
                    <p className="text-2xl font-semibold text-slate-900">{summary.claimedForms}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_16px_40px_-30px_rgba(15,23,42,0.45)]">
            <div className="flex flex-col gap-2 border-b border-slate-200 px-6 py-5 sm:px-8">
              <h2 className="text-lg font-semibold text-slate-900">My batch requests</h2>
              <p className="text-sm text-slate-500">
                A live table of your paper-form batch requests and verification outcomes.
              </p>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center gap-3 px-6 py-16 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin text-sky-600" />
                <span>Loading batch requests...</span>
              </div>
            ) : error || !response?.success ? (
              <div className="px-6 py-16 text-center sm:px-8">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">Unable to load batches</h3>
                <p className="mt-2 text-sm text-slate-500">
                  {error instanceof Error ? error.message : "Something went wrong while fetching batch requests."}
                </p>
              </div>
            ) : batches.length === 0 ? (
              <div className="px-6 py-16 text-center sm:px-8">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-sky-600">
                  <FileStack className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">No batch requests yet</h3>
                <p className="mt-2 text-sm text-slate-500">
                  When you create or submit paper-form batches, they will appear here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50/90">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Batch
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Forms
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Verification
                      </th>
                      {hasQualityScores && (<th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Quality
                      </th>)}
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Timeline
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200 bg-white">
                    {batches.map((batch) => {
                      const isDraftBatch = batch.status?.toUpperCase() === "DRAFT";
                      const isSubmittingDraft = submittingDraftBatchId === batch.id;

                      return (
                      <tr key={batch.id} className="transition hover:bg-slate-50/80">
                        <td className="px-6 py-5 align-top">
                          <div className="space-y-1">
                            <p className="text-sm font-semibold text-slate-900">Batch #{batch.id}</p>
                            <p className="text-xs text-slate-500">
                              Submitted {formatDateValue(batch.createdAt)}
                            </p>

                          </div>
                        </td>

                        <td className="px-6 py-5 align-top">
                          <div className="space-y-2">
                            <span
                              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(
                                batch.status
                              )}`}
                            >
                              {batch.status.replace(/_/g, " ")}
                            </span>
                            <p className="text-xs text-slate-500">
                              {batch.overdueFlag ? "Overdue for verification" : "Within verification timeline"}
                            </p>
                          </div>
                        </td>

                        <td className="px-6 py-5 align-top">
                          <div className="grid gap-2 text-sm text-slate-700">
                            <div className="flex items-center justify-between gap-6">
                              <span>Claimed</span>
                              <span className="font-semibold text-slate-900">{batch.claimedForms}</span>
                            </div>
                            <div className="flex items-center justify-between gap-6">
                              <span>Verified</span>
                              <span className="font-semibold text-emerald-700">{batch.verifiedForms}</span>
                            </div>
                            <div className="flex items-center justify-between gap-6">
                              <span>Rejected</span>
                              <span className="font-semibold text-rose-700">{batch.rejectedForms}</span>
                            </div>
                            <div className="flex items-center justify-between gap-6">
                              <span>Incomplete</span>
                              <span className="font-semibold text-amber-700">{batch.incompleteForms}</span>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-5 align-top">
                          <div className="space-y-2 text-sm text-slate-700">
                            <p>
                              <span className="text-slate-500">Mode:</span>{" "}
                              <span className="font-medium text-slate-900">
                                {batch.verificationMode || "Pending"}
                              </span>
                            </p>
                            
                           {batch.verifiedAt && ( <p>
                              <span className="text-slate-500">Verified:</span>{" "}
                              <span className="font-medium text-slate-900">
                                {formatDateValue(batch.verifiedAt)}
                              </span>
                            </p>)}
                          </div>
                        </td>

                        {hasQualityScores && (
                          <td className="px-6 py-5 align-top">
                            <div className="space-y-2 text-sm text-slate-700">
                              <p>
                                <span className="text-slate-500">Accuracy:</span>{" "}
                                <span className="font-medium text-slate-900">
                                  {formatScore(batch.accuracyScore)}
                                </span>
                              </p>
                              <p>
                                <span className="text-slate-500">Rejection rate:</span>{" "}
                                <span className="font-medium text-slate-900">
                                  {formatScore(batch.rejectionRate)}
                                </span>
                              </p>
                              <p>
                                <span className="text-slate-500">Timeliness:</span>{" "}
                                <span className="font-medium text-slate-900">
                                  {formatScore(batch.timelinessScore)}
                                </span>
                              </p>
                              <p>
                                <span className="text-slate-500">Final score:</span>{" "}
                                <span className="font-medium text-slate-900">
                                  {formatScore(batch.finalQualityScore)}
                                </span>
                              </p>
                            </div>
                          </td>
                        )}

                        <td className="px-6 py-5 align-top">
                          <div className="space-y-2 text-sm text-slate-700">
                           {batch.hoursToVerify && ( <p>
                              <span className="text-slate-500">Hours to verify:</span>{" "}
                              <span className="font-medium text-slate-900">
                                {batch.hoursToVerify ?? "--"}
                              </span>
                            </p>)}
                            <p className="max-w-xs text-slate-500">
                              {batch.notes?.trim() ? batch.notes : "No additional notes on this batch request."}
                            </p>
                          </div>
                        </td>

                        <td className="px-6 py-5 align-top">
                          {isDraftBatch ? (
                            <button
                              type="button"
                              onClick={() => setDraftSubmissionBatch(batch)}
                              disabled={isSubmittingDraft}
                              className="inline-flex min-w-[150px] items-center justify-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400"
                            >
                              {isSubmittingDraft && <Loader2 className="h-4 w-4 animate-spin" />}
                              {isSubmittingDraft ? "Submitting..." : "Submit draft batch"}
                            </button>
                          ) : (
                            <span className="text-xs font-medium text-slate-400">No action</span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>

      {draftSubmissionBatch && (
        <DraftBatchSubmissionModal
          draftBatch={draftSubmissionBatch}
          isSubmitting={submitDraftBatchMutation.isPending}
          title="Submit draft batch"
          description="Submit this paper-form draft batch to your supervisor for acknowledgement."
          helperText="Select the supervisor who should receive and acknowledge this batch."
          submitLabel="Submit batch"
          onClose={() => {
            if (!submitDraftBatchMutation.isPending) {
              setDraftSubmissionBatch(null);
            }
          }}
          onSubmit={handleSubmitBatchDraft}
        />
      )}
    </AdminLayout>
  );
};

export default CanvasserBatchRequests;
